class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.loadRecommendations();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  // The cart-drawer-recommendations element sits off-canvas (and can be scrolled
  // out of view once open), so its own IntersectionObserver may never see it
  // intersect the viewport. Force its load here instead, whenever the drawer
  // opens or its contents are re-rendered (e.g. right after an AJAX add-to-cart),
  // using whatever fresh instance is currently in the DOM.
  loadRecommendations() {
    this.querySelector('product-recommendations.cart-drawer-recommendations')?.loadRecommendations();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// "+" quick-add on the cart drawer's recommendation cards. Adds the variant via
// AJAX and re-renders the drawer in place — no form submit, so app scripts that
// hijack /cart/add forms (and redirect to checkout) never see it.
class CartRecommendationAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.onClick.bind(this));
  }

  onClick(evt) {
    evt.preventDefault();
    evt.stopPropagation();
    if (this.button.getAttribute('aria-disabled') === 'true') return;

    const cart = document.querySelector('cart-drawer');
    const spinner = this.querySelector('.loading-overlay__spinner');
    this.button.setAttribute('aria-disabled', true);
    this.button.classList.add('loading');
    spinner?.classList.remove('hidden');

    const body = {
      items: [{ id: Number(this.dataset.variantId), quantity: 1 }],
    };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error(response.description || response.message);
          return;
        }
        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-recommendation-add',
            productVariantId: this.dataset.variantId,
            cartData: response,
          });
        }
        cart.classList.remove('is-empty');
        cart.renderContents(response);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        // The drawer re-render usually replaces this element; reset in case it didn't.
        this.button.classList.remove('loading');
        this.button.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      });
  }
}

if (!customElements.get('cart-recommendation-add')) {
  customElements.define('cart-recommendation-add', CartRecommendationAdd);
}

function updateFreeShippingBar() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  const wrapper = drawer.querySelector('.cart-progress-wrapper');
  if (!wrapper) return;

  const baseThreshold = parseFloat(wrapper.dataset.baseThreshold);
  if (!baseThreshold) return;
  const rate = window.Shopify?.currency?.rate || 1;

  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      const cartTotal = cart.total_price / 100;
      const convertedThreshold = baseThreshold * rate;
      const remaining = convertedThreshold - cartTotal;
      const progress = Math.min((cartTotal / convertedThreshold) * 100, 100);

      const fill = wrapper.querySelector('.cart-progress-fill');
      const thumb = wrapper.querySelector('.cart-progress-thumb');
      const message = wrapper.querySelector('.cart-progress-message');

      if (fill) fill.style.width = `${progress}%`;
      if (thumb) thumb.style.left = `${progress}%`;

      if (message) {
        if (remaining > 0) {
          const formattedRemaining = new Intl.NumberFormat(Shopify.locale, {
            style: 'currency',
            currency: Shopify.currency.active,
          }).format(remaining);
          message.innerHTML = `You're <strong>${formattedRemaining}</strong> Away From <strong>FREE SHIPPING!</strong>`;
        } else {
          message.innerHTML = `<strong>You've unlocked FREE SHIPPING!</strong> 🎉`;
        }
      }
    })
    .catch((e) => {
      console.error(e);
    });
}

document.addEventListener('DOMContentLoaded', () => {
  updateFreeShippingBar();

  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  new MutationObserver(updateFreeShippingBar).observe(drawer, {
    childList: true,
    subtree: true,
  });
});
